import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, FileText, Plus, X, Search, Truck,
  Package, ScanBarcode, Check, AlertCircle, Trash2, Users
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const CHALLAN_TYPES = [
  { value: 'SUPPLY_ON_APPROVAL', label: 'Supply on Approval', desc: 'Goods sent for customer approval' },
  { value: 'JOB_WORK', label: 'Job Work', desc: 'Goods sent to job worker' },
  { value: 'CONSIGNMENT', label: 'Consignment / Stock Transfer', desc: 'Internal transfer between branches' },
  { value: 'EXHIBITION', label: 'Exhibition / Demo', desc: 'Goods sent for display or demo' },
  { value: 'RETURNABLE', label: 'Returnable Delivery', desc: 'Goods expected to be returned' },
  { value: 'NON_RETURNABLE', label: 'Non-Returnable Delivery', desc: 'Goods not expected to return' },
  { value: 'REPAIR', label: 'Repair / Service', desc: 'Goods sent for repair or servicing' },
  { value: 'SAMPLE', label: 'Sample Delivery', desc: 'Free samples (no invoice)' },
  { value: 'OTHER', label: 'Other', desc: 'Custom reason' }
];

// Barcode Scanner Modal
const BarcodeScannerModal = ({ isOpen, onClose, onScan, products }) => {
  const [barcode, setBarcode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleScan = () => {
    if (!barcode.trim()) return;
    const product = products.find(p => 
      p.barcode === barcode || 
      p.code?.toLowerCase() === barcode.toLowerCase() ||
      p.sku?.toLowerCase() === barcode.toLowerCase()
    );
    if (product) {
      onScan(product);
      setSuccess(`Added: ${product.name}`);
      setError('');
      setBarcode('');
      setTimeout(() => setSuccess(''), 2000);
      inputRef.current?.focus();
    } else {
      setError('Item not found. Try again.');
      setSuccess('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <ScanBarcode className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-slate-800">Scan Item</h3>
              <p className="text-[12px] text-slate-500">Scan barcode or type manually</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-6">
          <div className="relative">
            <input 
              ref={inputRef} 
              type="text" 
              value={barcode}
              onChange={(e) => { setBarcode(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleScan()}
              className="w-full px-4 py-3 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-[14px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
              placeholder="Scan barcode or type and press Enter" 
              autoFocus
            />
            <button onClick={handleScan} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Check className="w-4 h-4" />
            </button>
          </div>
          {success && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-[13px] text-emerald-700 font-medium">{success}</span>
            </div>
          )}
          {error && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-[13px] text-red-700">{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CreateDeliveryChallanPage = () => {
  const { businessId, challanId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const isEditing = !!challanId;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const customerSearchRef = useRef(null);
  const itemSearchRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    challan_date: new Date().toISOString().split('T')[0],
    challan_type: 'SUPPLY_ON_APPROVAL',
    reference_no: '',
    vehicle_no: '',
    transporter_name: '',
    lr_number: '',
    place_of_supply: '',
    items: [],
    customer_notes: '',
    terms_and_conditions: 'Goods once delivered cannot be returned without prior approval.\nAll disputes subject to local jurisdiction.',
    internal_notes: ''
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchCustomers();
      fetchProducts();
      if (isEditing) fetchChallan();
    }
  }, [bid, challanId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerSearchRef.current && !customerSearchRef.current.contains(e.target)) {
        setShowCustomerDropdown(false);
      }
      if (itemSearchRef.current && !itemSearchRef.current.contains(e.target)) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchChallan = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/delivery-challans/${challanId}`);
      const data = response.data;
      // Backend returns real column names (party_*, vehicle_number,
      // transport_details, notes, plus the form-specific extras reference_no,
      // lr_number, place_of_supply, terms_and_conditions, internal_notes
      // added in the 20260416_1340 migration). Map them into the form state.
      setFormData({
        customer_id: data.party_id || '',
        challan_date: data.challan_date?.split('T')[0] || '',
        challan_type: data.challan_type || 'SUPPLY_ON_APPROVAL',
        reference_no: data.reference_no || '',
        vehicle_no: data.vehicle_number || '',
        transporter_name: data.transport_details || '',
        lr_number: data.lr_number || '',
        place_of_supply: data.place_of_supply || '',
        items: data.items || [],
        customer_notes: data.notes || '',
        terms_and_conditions: data.terms_and_conditions || '',
        internal_notes: data.internal_notes || ''
      });
      setSelectedCustomer({ name: data.party_name, id: data.party_id });
      setCustomerSearch(data.party_name || '');
    } catch (error) {
      console.error('Error fetching challan:', error);
      toast.error('Failed to load challan');
    }
    finally { setLoading(false); }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/parties?type=customer');
      const data = response.data;
      setCustomers(data.parties || data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load customers'));
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/items');
      const data = response.data;
      setProducts(data.data || data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load products'));
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.party_id || customer.id,
      place_of_supply: customer.state || prev.place_of_supply
    }));
  };

  const addItem = (product = null) => {
    if (product) {
      const existingIndex = formData.items.findIndex(item => item.item_id === product.id);
      if (existingIndex >= 0) {
        updateItem(existingIndex, 'quantity', (formData.items[existingIndex].quantity || 1) + 1);
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          item_id: product.id,
          item_name: product.name,
          item_code: product.code || '',
          hsn_code: product.hsn_code || '',
          batch_no: '',
          mfg_date: '',
          expiry_date: '',
          quantity: 1,
          unit: product.unit || 'Nos',
          rate: product.selling_price || product.sale_price || 0,
          amount: product.selling_price || product.sale_price || 0
        }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          item_id: '',
          item_name: '',
          item_code: '',
          hsn_code: '',
          batch_no: '',
          mfg_date: '',
          expiry_date: '',
          quantity: 1,
          unit: 'Nos',
          rate: 0,
          amount: 0
        }]
      }));
    }
    setItemSearch('');
    setShowItemDropdown(false);
  };

  const removeItem = (index) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      if (field === 'item_id') {
        const product = products.find(p => p.id === value);
        if (product) {
          newItems[index].item_name = product.name;
          newItems[index].item_code = product.code || '';
          newItems[index].hsn_code = product.hsn_code || '';
          newItems[index].rate = product.selling_price || product.sale_price || 0;
          newItems[index].unit = product.unit || 'Nos';
        }
      }

      // Recalculate amount
      const qty = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      newItems[index].amount = Math.round(qty * rate * 100) / 100;

      return { ...prev, items: newItems };
    });
  };

  // Calculate totals
  const totalItems = formData.items.length;
  const totalQuantity = formData.items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const totalValue = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);

  const handleSave = async (asDraft = true) => {
    if (!formData.customer_id) { 
      toast.error('Please select a customer'); 
      return; 
    }
    if (formData.items.length === 0) { 
      toast.error('Please add at least one item'); 
      return; 
    }

    setSaving(true);
    try {
      // Map form state (UI labels use customer_* / vehicle_no / transporter_name
      // / customer_notes) to the real delivery_challans column names the
      // backend stores.
      const payload = {
        party_id: formData.customer_id,
        challan_date: formData.challan_date,
        challan_type: formData.challan_type,
        vehicle_number: formData.vehicle_no || null,
        transport_details: formData.transporter_name || null,
        reference_no: formData.reference_no || null,
        lr_number: formData.lr_number || null,
        place_of_supply: formData.place_of_supply || null,
        notes: formData.customer_notes || null,
        terms_and_conditions: formData.terms_and_conditions || null,
        internal_notes: formData.internal_notes || null,
        items: formData.items,
        save_as_draft: asDraft
      };

      const response = isEditing
        ? await api.put(`/delivery-challans/${challanId}`, payload)
        : await api.post('/delivery-challans', payload);

      const data = response.data;
      toast.success(isEditing ? 'Delivery challan updated' : 'Delivery challan created');
      navigate(`/business/${bid}/delivery-challans/${data.challan?.id || challanId}`);
    } catch (error) {
      console.error('Error saving challan:', error);
      toast.error(getApiErrorMessage(error, 'Error saving challan'));
    }
    finally { setSaving(false); }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile?.includes(customerSearch) ||
    c.email?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* ══════════════════════════════════════════════════════════════════════
          PREMIUM HEADER - Commercial Grade Design
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* LEFT: Back Button + Title */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/business/${bid}/delivery-challans`)}
                className="group flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <h1 className="text-[18px] font-semibold text-slate-800">
                  {isEditing ? 'Edit Delivery Challan' : 'New Delivery Challan'}
                </h1>
                <p className="text-[12px] text-slate-500 mt-0.5">Create delivery documentation for goods movement</p>
              </div>
            </div>
            
            {/* RIGHT: Action Buttons */}
            <div className="flex items-center gap-2">
              <button 
                onClick={() => handleSave(true)} 
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
              >
                <FileText className="w-4 h-4" /> Save Draft
              </button>
              <button 
                onClick={() => handleSave(false)} 
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-emerald-600/20 active:scale-[0.98]"
              >
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Issue Challan'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MAIN CONTENT AREA - Premium Layout
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - 8 cols */}
            <div className="col-span-8 space-y-5">
              
              {/* CARD A: CUSTOMER INFORMATION */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Customer Information</h2>
                </div>
                
                <div className="relative" ref={customerSearchRef}>
                  <label className="block text-[12px] text-slate-500 mb-1.5">Search Customer *</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                      onFocus={() => setShowCustomerDropdown(true)}
                      className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
                      placeholder="Search by name, mobile, email..."
                    />
                  </div>
                  
                  {showCustomerDropdown && filteredCustomers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCustomers.slice(0, 8).map(customer => {
                        const contact = [customer.mobile, customer.email].filter(Boolean).join(' • ');
                        return (
                          <div
                            key={customer.party_id || customer.id}
                            onClick={() => handleCustomerSelect(customer)}
                            className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[13px] font-medium text-slate-800 truncate">{customer.name}</p>
                              {contact && <p className="text-[11px] text-slate-500 shrink-0 truncate">{contact}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedCustomer && (() => {
                  const selContact = [selectedCustomer.mobile, selectedCustomer.email].filter(Boolean).join(' • ');
                  return (
                  <div className="mt-4 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <p className="text-[13px] font-medium text-slate-800 truncate">{selectedCustomer.name}</p>
                        {selContact && <p className="text-[11px] text-slate-500 truncate">{selContact}</p>}
                      </div>
                      <button
                        onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setFormData(prev => ({ ...prev, customer_id: '' })); }}
                        className="text-slate-400 hover:text-slate-600 shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  );
                })()}
              </div>

              {/* CARD B: CHALLAN DETAILS */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Truck className="w-4 h-4 text-blue-600" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Challan Details</h2>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Challan Date *</label>
                    <input 
                      type="date" 
                      value={formData.challan_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, challan_date: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Reference / PO No</label>
                    <input 
                      type="text" 
                      value={formData.reference_no}
                      onChange={(e) => setFormData(prev => ({ ...prev, reference_no: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="Customer PO or reference"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-[12px] text-slate-500 mb-1.5">Challan Type * (Important for GST)</label>
                  <select
                    value={formData.challan_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, challan_type: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                  >
                    {CHALLAN_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {CHALLAN_TYPES.find(t => t.value === formData.challan_type)?.desc}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Vehicle No</label>
                    <input 
                      type="text" 
                      value={formData.vehicle_no}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicle_no: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="e.g., MH12AB1234"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Transporter Name</label>
                    <input 
                      type="text" 
                      value={formData.transporter_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, transporter_name: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="Transport company name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">LR Number</label>
                    <input 
                      type="text" 
                      value={formData.lr_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, lr_number: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="Lorry Receipt Number"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Place of Supply</label>
                    <input 
                      type="text" 
                      value={formData.place_of_supply}
                      onChange={(e) => setFormData(prev => ({ ...prev, place_of_supply: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="State"
                    />
                  </div>
                </div>
              </div>

              {/* CARD C: ITEMS */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Package className="w-4 h-4 text-blue-600" />
                    </div>
                    <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Items</h2>
                    <span className="text-[12px] text-slate-400 ml-1">{totalItems} items added</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setShowScanner(true)}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-all text-[12px] font-medium"
                    >
                      <ScanBarcode className="w-4 h-4" /> Scan Barcode
                    </button>
                    <button 
                      onClick={() => addItem()}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all text-[12px] font-medium"
                    >
                      <Plus className="w-4 h-4" /> Add Item
                    </button>
                  </div>
                </div>

                {/* Quick Add Row */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 relative" ref={itemSearchRef}>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={itemSearch}
                      onChange={(e) => { setItemSearch(e.target.value); setShowItemDropdown(true); }}
                      onFocus={() => setShowItemDropdown(true)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
                      placeholder="Quick add item by name or code..."
                    />
                    {showItemDropdown && filteredProducts.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                        {filteredProducts.slice(0, 8).map(product => (
                          <div
                            key={product.id}
                            onClick={() => addItem(product)}
                            className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[13px] font-medium text-slate-800 truncate">{product.name}</p>
                              {product.code && <p className="text-[11px] text-slate-500 shrink-0">{product.code}</p>}
                            </div>
                          </div>
                        ))}
                        {filteredProducts.length > 8 && (
                          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 text-center">
                            Showing 8 of {filteredProducts.length} — type to narrow
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Items Table */}
                {formData.items.length === 0 ? (
                  <div className="text-center py-10 border border-dashed border-slate-200 rounded-lg bg-slate-50/30">
                    <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-[13px] text-slate-500">No items added yet</p>
                    <p className="text-[12px] text-slate-400 mt-1">Search for items or scan barcode to add</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">#</th>
                          <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">Item</th>
                          <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">HSN/SAC</th>
                          <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">Batch</th>
                          <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-slate-500 uppercase">Qty</th>
                          <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-slate-500 uppercase">Unit</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-500 uppercase">Rate</th>
                          <th className="px-3 py-2.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => (
                          <tr key={index} className="border-b border-slate-100 hover:bg-slate-50/50">
                            <td className="px-3 py-2.5 text-[12px] text-slate-500">{index + 1}</td>
                            <td className="px-3 py-2.5">
                              <select 
                                value={item.item_id} 
                                onChange={(e) => updateItem(index, 'item_id', e.target.value)}
                                className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] focus:outline-none focus:border-blue-500"
                              >
                                <option value="">Select Item</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                            </td>
                            <td className="px-3 py-2.5">
                              <input 
                                type="text" 
                                value={item.hsn_code || ''} 
                                onChange={(e) => updateItem(index, 'hsn_code', e.target.value)}
                                className="w-20 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-center focus:outline-none focus:border-blue-500" 
                                placeholder="—" 
                              />
                            </td>
                            <td className="px-3 py-2.5">
                              <input 
                                type="text" 
                                value={item.batch_no || ''} 
                                onChange={(e) => updateItem(index, 'batch_no', e.target.value)}
                                className="w-20 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-center focus:outline-none focus:border-blue-500" 
                                placeholder="—" 
                              />
                            </td>
                            <td className="px-3 py-2.5">
                              <input 
                                type="number" 
                                value={item.quantity} 
                                onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-center focus:outline-none focus:border-blue-500" 
                                min="0" 
                              />
                            </td>
                            <td className="px-3 py-2.5">
                              <input 
                                type="text" 
                                value={item.unit} 
                                onChange={(e) => updateItem(index, 'unit', e.target.value)}
                                className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-center focus:outline-none focus:border-blue-500" 
                              />
                            </td>
                            <td className="px-3 py-2.5">
                              <input 
                                type="number" 
                                value={item.rate} 
                                onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                className="w-24 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-right focus:outline-none focus:border-blue-500" 
                                min="0" 
                              />
                            </td>
                            <td className="px-3 py-2.5">
                              <button 
                                onClick={() => removeItem(index)} 
                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* CARD E: NOTES & TERMS */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-4">Notes & Terms</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Customer Notes (printed on challan)</label>
                    <textarea 
                      value={formData.customer_notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_notes: e.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none resize-none"
                      rows={2} 
                      placeholder="Notes visible to customer..."
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Terms & Conditions</label>
                    <textarea 
                      value={formData.terms_and_conditions}
                      onChange={(e) => setFormData(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none resize-none"
                      rows={3} 
                      placeholder="Standard terms..."
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Internal Notes (not printed)</label>
                    <textarea 
                      value={formData.internal_notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, internal_notes: e.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none resize-none"
                      rows={2} 
                      placeholder="Internal team notes..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - 4 cols: SUMMARY */}
            <div className="col-span-4 space-y-5">
              <div className="bg-amber-50 rounded-xl p-5 shadow-sm border border-amber-200/60 sticky top-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Package className="w-4 h-4 text-amber-700" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Total Items</span>
                    <span className="text-slate-800 font-medium">{totalItems}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Total Quantity</span>
                    <span className="text-slate-800 font-semibold text-[15px]">{totalQuantity}</span>
                  </div>
                  
                  {totalValue > 0 && (
                    <>
                      <div className="border-t border-amber-200 pt-3 mt-3">
                        <div className="flex justify-between text-[12px] text-slate-500">
                          <span>Estimated Value (optional)</span>
                          <span>₹{totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="border-t border-amber-200 pt-3 mt-3">
                    <p className="text-[11px] text-amber-700">
                      ⚠️ Delivery Challan is a logistics document. No tax is calculated.
                    </p>
                  </div>
                </div>
              </div>

              {/* Challan Type Info Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <h3 className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide mb-3">Challan Type Info</h3>
                <div className="space-y-2">
                  <p className="text-[12px] text-slate-700 font-medium">
                    {CHALLAN_TYPES.find(t => t.value === formData.challan_type)?.label}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {CHALLAN_TYPES.find(t => t.value === formData.challan_type)?.desc}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal 
        isOpen={showScanner} 
        onClose={() => setShowScanner(false)}
        onScan={(product) => addItem(product)} 
        products={products}
      />
    </div>
  );
};

export default CreateDeliveryChallanPage;
